import {
  allBrandRequestStatuses,
  baseURL,
  brandRequestStatusObject,
  Button,
  DropdownButton,
  generateLinkWithParams,
  getSourcingStatusList,
  htmlToPlainText,
  ItemSplitter,
  Link,
  linkPlaceholders,
  navLinks,
  routing,
  Tab,
  TabContainer,
  TabDisplay,
  TableView,
  Tabs,
  TextStyledLink,
  TopBar,
  useBrandRequests,
  useCategories,
  useLoginContext,
  useModalContext,
  useVendors,
} from "common";
import React, { useEffect, useState } from "react";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import SentEmailsDetail from "../SentEmailsDetail";
import StatusChangeForm from "./StatusChangeForm";
import { Container, VendorField } from "./styles";
import UpdateFollowUpDate from "./UpdateFollowUpDate";
import VendorAddForm from "./VendorAddForm";

const ALL = "ALL";
const UNWORKED = "UNWORKED";
const allStatuses = allBrandRequestStatuses;
allStatuses.pop();

const allUnworkedStatuses = [
  brandRequestStatusObject.Unworked.value,
  brandRequestStatusObject.NoneAvailability.value,
];

const downloadExport = (status, selectedCategories) => {
  const categoriesParam = selectedCategories.length
    ? `&categories=${selectedCategories.join(",")}`
    : "";
  const a = document.createElement("a");
  a.target = "_blank";
  a.href = `${baseURL}/brandRequests/export?status=${status}${categoriesParam}`;
  a.click();
  a.remove();
};

const BrandRequestList = () => {
  const [p] = useSearchParams();
  const selectedStatus = p.get("status");
  const selectedPriorityLevel = p.get("priorityLevel");
  const isPinnedSelected = p.get("pinned");
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { brandRequests, refetch, loading } = useBrandRequests();
  const { isSourcingAdmin } = useLoginContext();
  const adminProps = isSourcingAdmin
    ? {
        deleteUrl: (id) => `/brandRequests/${id}`,
        deleteMessage: (br) => `Delete ${br.brandName}`,
        pinUrl: (id) => `/brandRequests/pin/${id}`,
      }
    : {};
  const { categories } = useCategories();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const priorityLevels = [
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
    { value: 5, label: "5" },
  ];
  const vendors = useVendors();
  const { setModalContent } = useModalContext();

  useEffect(() => {
    sessionStorage.setItem("lastSelectedBrandRequestsTable", selectedStatus);
  }, [selectedStatus]);

  const statuses = getSourcingStatusList(brandRequests);
  const hiddenStatusTabs = [
    brandRequestStatusObject.Unworked.value,
    brandRequestStatusObject.InActive.value,
    brandRequestStatusObject.Ordered.value,
    brandRequestStatusObject.AlreadyOnTheMarket.value,
    brandRequestStatusObject.VendorCreated.value,
    // Follow Up is merged into the Worked On tab, so it has no tab of its own.
    brandRequestStatusObject.FollowUp.value,
  ];
  const statusTabs = statuses.filter(
    ({ value }) => !hiddenStatusTabs.includes(value),
  );
  const isAll = selectedStatus === ALL;
  const isUnworked = selectedStatus === UNWORKED;
  const isFollowUp = selectedStatus === brandRequestStatusObject.FollowUp.value;
  const isWorkedOn = selectedStatus === brandRequestStatusObject.WorkedOn.value;
  // The Worked On tab also contains Follow Up items.
  const workedOnStatuses = [
    brandRequestStatusObject.WorkedOn.value,
    brandRequestStatusObject.FollowUp.value,
  ];

  const defaultStatusFilters = {
    statuses: isAll
      ? { $in: allStatuses }
      : isUnworked
        ? { $in: allUnworkedStatuses }
        : isWorkedOn
          ? { $in: workedOnStatuses }
          : selectedStatus,
  };

  const defaultPinnedFilters = {
    isPinned: true,
    statuses: { $in: allStatuses },
  };

  const defaultFilters = {
    ...((isPinnedSelected && defaultPinnedFilters) || defaultStatusFilters),
    ...(selectedPriorityLevel && {
      priorityLevel: Number(selectedPriorityLevel),
    }),
  };

  const filterConfig = [
    {
      name: "brandName",
      type: "input",
      label: "Search",
    },
    {
      name: "category",
      type: "dropdown",
      options: categories,
      label: "Filter By Category",
    },
    {
      name: "vendor",
      type: "dropdown",
      options: vendors,
      label: "Filter By Vendor",
    },
    {
      name: "priorityLevel",
      type: "dropdown",
      options: priorityLevels,
      label: "Filter By Priority Level",
    },
  ];
  const styles = {
    checkboxContainer: {
      display: "flex",
      alignItems: "center",
      marginBottom: "5px",
    },
    checkboxLabel: {
      marginLeft: "8px",
    },
  };
  const exportOptions = [
    <div onMouseDown={() => downloadExport("AllWorkedOn", selectedCategories)}>
      All Worked On
    </div>,
    ...statuses.map(({ value, label }) => {
      return (
        <div
          key={value}
          onMouseDown={() => downloadExport(value, selectedCategories)}
        >
          {label}
        </div>
      );
    }),
  ];

  const exportCategoryOptions = [
    ...categories.map((category) => (
      <div
        key={category.value}
        style={styles.checkboxContainer}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <input
          type="checkbox"
          checked={selectedCategories.includes(category.value)}
          onChange={() => toggleCategorySelection(category.value)}
        />
        <span style={styles.checkboxLabel}>{category.label}</span>
      </div>
    )),
  ];
  const toggleCategorySelection = (categoryId) => {
    setSelectedCategories((prev) => {
      const currentIndex = prev.indexOf(categoryId);
      if (currentIndex === -1) {
        return [...prev, categoryId];
      } else {
        return prev.filter((id) => id !== categoryId);
      }
    });
  };
  const navigateTo = (status = ALL) => {
    const categoriesParam =
      selectedCategories.length > 0 ? selectedCategories.join(",") : null;
    const searchParams = { status };
    if (categoriesParam) {
      searchParams.categories = categoriesParam;
    }
    navigate({ pathname, search: `?${createSearchParams(searchParams)}` });
  };

  const navigateToPinned = () => {
    navigate({
      pathname,
      search: `?${createSearchParams({ status: ALL, pinned: 1 })}`,
    });
  };

  if (!selectedStatus) {
    navigateTo();
  }

  const renderTable = () => {
    return (
      <TableView
        tableName="brandRequestList"
        selectView
        selectedStatus={selectedStatus}
        to={routing.brandRequests.view}
        bulkActions={[
          { name: "Assign Vendors", Component: VendorAddForm },
          { name: "Change Status", Component: StatusChangeForm },
          {
            name: "Move Follow Up Date",
            Component: UpdateFollowUpDate,
            componentProps: { refetch },
            isSingle: true,
          },
        ]}
        url="/brandRequests"
        tableConfig={[
          {
            name: "brandName",
            header: "Brand Name",
          },
          {
            name: "category",
            header: "Category",
          },
          {
            name: "requestedByCustomer",
            header: "Customer",
          },
          ...(isAll || isUnworked
            ? [{ name: "statuses", header: "Statuses" }]
            : []),
          {
            name: "requestBy",
            header: "Requested By",
          },
          {
            name: "createdAt",
            header: "Requested Date",
            isDate: true,
          },
          {
            name: "priorityLevel",
            header: "Priority Level",
          },
          {
            name: "lastEmail",
            header: "Last Email",
            width: "300px",
          },
          ...(isFollowUp || isWorkedOn
            ? [
                {
                  name: "followUpDate",
                  header: "Follow Up Date",
                  isDate: true,
                },
              ]
            : []),
          {
            name: "vendors",
            header: "Assigned To",
            noTo: true,
          },
        ]}
        filterConfig={filterConfig}
        darker
        navLinks={navLinks.brandRequests(isSourcingAdmin)}
        linkParam={linkPlaceholders.brandRequestId}
        noHeader
        initialSort={JSON.stringify({ isPinned: -1, createdAt: -1 })}
        onActionComplete={refetch}
        {...adminProps}
        height="55vh"
        defaultParams={{
          populate: JSON.stringify([
            { path: "user" },
            { path: "category" },
            {
              path: "vendorRequests",
              populate: "vendor sentEmails.contacts",
            },
          ]),
        }}
        defaultFilters={defaultFilters}
        shapeData={(res) =>
          res.data.data.map((d) => ({
            ...d,
            requestBy: d.user?.name || d.requestBy,
            category: d.category?.name,
            statuses: d.statuses
              .map((status) => brandRequestStatusObject[status].label)
              .join(", "),
            vendors: d.vendorRequests.map((v) => (
              <Link
                to={generateLinkWithParams(routing.vendors.view, {
                  [linkPlaceholders.vendorId]: v.vendor.id,
                })}
              >
                <VendorField>{v.vendor.name}</VendorField>
              </Link>
            )),
            lastEmail: d?.vendorRequests.map((vendorRequest) => (
              <div style={{ marginBottom: "5px" }}>
                {vendorRequest?.sentEmails?.length !== 0 ? (
                  <>
                    <p>
                      <b>{vendorRequest.vendor.name}</b>
                    </p>
                    <p className="truncate">
                      <b>
                        {
                          vendorRequest.sentEmails[
                            vendorRequest.sentEmails.length - 1
                          ].subject
                        }
                        :
                      </b>{" "}
                      {htmlToPlainText(
                        vendorRequest.sentEmails[
                          vendorRequest.sentEmails.length - 1
                        ].body,
                      )}
                    </p>
                    <TextStyledLink
                      onClick={(e) => {
                        e.stopPropagation();
                        setModalContent(
                          <SentEmailsDetail
                            vendor={vendorRequest.vendor}
                            sentEmails={vendorRequest.sentEmails}
                          />,
                        );
                      }}
                    >
                      View More
                    </TextStyledLink>
                  </>
                ) : null}
              </div>
            )),
          }))
        }
        initialSkip={
          sessionStorage.getItem("lastSelectedBrandRequestsTable") ===
          selectedStatus
            ? Number(sessionStorage.getItem("brandRequestList-skip") || 0)
            : 0
        }
        initialLimit={
          sessionStorage.getItem("lastSelectedBrandRequestsTable") ===
          selectedStatus
            ? Number(sessionStorage.getItem("brandRequestList-limit") || 10)
            : 10
        }
        initialFilters={
          sessionStorage.getItem("lastSelectedBrandRequestsTable") ===
          selectedStatus
            ? (JSON.parse(sessionStorage.getItem("brandRequestList-filters")) ??
              null)
            : null
        }
      />
    );
  };

  return (
    <Container>
      <TopBar>
        <h1>Brands</h1>
        <div>
          <ItemSplitter style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}>
            <DropdownButton secondary options={exportCategoryOptions}>
              Export Category
            </DropdownButton>
            <DropdownButton secondary options={exportOptions}>
              Export Report
            </DropdownButton>
            <Link to={routing.brandRequests.add}>
              <Button>Request A Brand</Button>
            </Link>
            <Link to={routing.brandRequests.bulkImport}>
              <Button>Bulk Import</Button>
            </Link>
          </ItemSplitter>
        </div>
      </TopBar>
      <TabContainer>
        <Tabs>
          {/* <Tab
            isActive={isDashboard && !isPinnedSelected}
            onClick={() => navigateTo(DASHBOARD)}
          >
            Dashboard
            <sup>{statusChanges?.length || 0}</sup>
          </Tab>
          <Tab
            isActive={isThirtyDaysNoChange && !isPinnedSelected}
            onClick={() => navigateTo(thirtyDaysNoChange)}
          >
            30-Day No Change
            <sup>{thirtyDaysNoChangeRecords?.length || 0}</sup>
          </Tab>
          <Tab
            isActive={isSixtyDaysNoChange && !isPinnedSelected}
            onClick={() => navigateTo(sixtyDaysNoChange)}
          >
            60-Day No Change
            <sup>{sixtyDaysNoChangeRecords?.length || 0}</sup>
          </Tab> */}
          <Tab
            isActive={isAll && !isPinnedSelected}
            onClick={() => navigateTo(ALL)}
          >
            All Working On
            <sup>
              {loading
                ? "..."
                : Object.keys(
                    statuses.reduce(
                      (acc, { ids, value }) => ({
                        ...acc,
                        ...(allStatuses.includes(value) ? ids : {}),
                      }),
                      {},
                    ),
                  ).length}
            </sup>
          </Tab>
          <Tab
            isActive={isUnworked && !isPinnedSelected}
            onClick={() => navigateTo(UNWORKED)}
          >
            Unworked
            <sup>
              {loading
                ? "..."
                : Object.keys(
                    statuses.reduce(
                      (acc, { ids, value }) => ({
                        ...acc,
                        ...(allUnworkedStatuses.includes(value) ? ids : {}),
                      }),
                      {},
                    ),
                  ).length}
            </sup>
          </Tab>
          {statusTabs.map(({ value, total, label }) => {
            const displayTotal =
              value === brandRequestStatusObject.WorkedOn.value
                ? Object.keys(
                    statuses.reduce(
                      (acc, { ids, value: v }) => ({
                        ...acc,
                        ...(workedOnStatuses.includes(v) ? ids : {}),
                      }),
                      {},
                    ),
                  ).length
                : total;
            return (
              <Tab
                key={value}
                isActive={value === selectedStatus && !isPinnedSelected}
                onClick={() => navigateTo(value)}
              >
                {label}
                <sup>{loading ? "..." : displayTotal}</sup>
              </Tab>
            );
          })}
          <Tab isActive={isPinnedSelected} onClick={navigateToPinned}>
            Pinned
            <sup>
              {loading
                ? "..."
                : brandRequests.filter((br) => br.isPinned === true).length}
            </sup>
          </Tab>
        </Tabs>
        <TabDisplay>{renderTable()}</TabDisplay>
      </TabContainer>
    </Container>
  );
};

export default BrandRequestList;
